import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLeads, Lead } from '../../crm-leads/useData';

export interface OpenedLeadsProps {
  limit?: number;
  embedded?: boolean;
}

const OpenedLeads: React.FC<OpenedLeadsProps> = ({ limit = 5, embedded }) => {
  const navigate = useNavigate();
  const { leads, isLoading } = useLeads();
  const cardClass = embedded ? 'min-w-0 flex-1 flex flex-col min-h-0' : 'bg-white dark:bg-gray-800 rounded-xl shadow-sm p-5 border border-gray-200/80 dark:border-gray-600/50';
  const listClass = embedded ? 'flex-1 min-h-0 overflow-y-auto space-y-2' : 'space-y-2 max-h-48 sm:max-h-64 overflow-y-auto';

  const openedLeads = React.useMemo(() => {
    return (leads || [])
      .filter((lead: Lead) => {
        const status = lead.leadStatusDescr?.as_descr || '';
        return status.toLowerCase().includes('opened');
      })
      .sort((a: Lead, b: Lead) => {
        // Sort by contacted date (most recent first), then by created date
        const dateA = new Date(a.leadContactedDate || a.leadCreatedAt || 0).getTime();
        const dateB = new Date(b.leadContactedDate || b.leadCreatedAt || 0).getTime();
        return dateB - dateA;
      })
      .slice(0, limit);
  }, [leads, limit]);

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatCurrency = (value?: number): string => {
    if (value === undefined || value === null) return '€0.00';
    return `€${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getStatusBadgeColor = (status?: string) => {
    if (!status) return { bg: 'bg-gray-100', text: 'text-gray-800' };
    const statusLower = status.toLowerCase();
    if (statusLower.includes('opened')) {
      return { bg: 'bg-blue-100', text: 'text-blue-800' };
    } else if (statusLower.includes('closed')) {
      return { bg: 'bg-green-100', text: 'text-green-800' };
    } else if (statusLower.includes('canceled') || statusLower.includes('cancelled')) {
      return { bg: 'bg-red-100', text: 'text-red-800' };
    }
    return { bg: 'bg-gray-100', text: 'text-gray-800' };
  };

  if (isLoading) {
    return (
      <div className={cardClass}>
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-3 flex-shrink-0">Opened Leads</h2>
        <div className={listClass}>
          {[1, 2, 3].map((item) => (
            <div key={item} className="flex items-center space-x-2 sm:space-x-3 pb-2 border-b border-gray-100 last:border-0">
              <div className="h-7 w-7 sm:h-8 sm:w-8 rounded-full bg-gray-200 animate-pulse flex-shrink-0"></div>
              <div className="flex-1 min-w-0">
                <div className="h-3 bg-gray-200 animate-pulse rounded w-3/4 mb-1 sm:mb-1.5"></div>
                <div className="h-2 sm:h-2.5 bg-gray-200 animate-pulse rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={cardClass}>
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Opened Leads</h2>
        <button
          onClick={() => navigate('/leads')}
          className="text-sm text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300 font-medium whitespace-nowrap transition-colors"
        >
          View All
        </button>
      </div>
      <div className={listClass}>
        {openedLeads.length > 0 ? (
          openedLeads.map((lead: Lead) => {
            const status = lead.leadStatusDescr?.as_descr || '';
            const { bg, text } = getStatusBadgeColor(status);
            
            return (
              <div
                key={lead.leadId}
                onClick={() => navigate(`/leads/${lead.leadId}`)}
                className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer transition-colors border-b border-gray-100 dark:border-gray-700/50 last:border-0"
              >
                <div className="flex-shrink-0">
                  <div className="h-7 w-7 sm:h-8 sm:w-8 rounded-full bg-teal-50 text-teal-700 ring-1 ring-teal-200/70 dark:bg-teal-900/30 dark:text-teal-300 dark:ring-teal-800/50 flex items-center justify-center text-[10px] sm:text-xs font-semibold">
                    {lead.client?.clientName?.charAt(0).toUpperCase() || 'L'}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] sm:text-xs font-medium text-gray-900 truncate">
                    {lead.client?.clientName || `Client #${lead.clientId}`}
                  </p>
                  <div className="flex items-center gap-1.5 sm:gap-2 mt-0.5 flex-wrap">
                    {status && (
                      <span className={`inline-flex px-1 sm:px-1.5 py-0.5 text-[10px] sm:text-xs font-semibold rounded-full ${bg} ${text}`}>
                        {status}
                      </span>
                    )}
                    {lead.leadValue && (
                      <span className="text-[10px] sm:text-xs text-gray-500">
                        {formatCurrency(lead.leadValue)}
                      </span>
                    )}
                  </div>
                  {lead.leadContactedDate && (
                    <p className="text-[10px] sm:text-xs text-gray-400 mt-0.5">
                      Contacted: {formatDate(lead.leadContactedDate)}
                    </p>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-4 sm:py-6 text-gray-500">
            <svg className="w-8 h-8 sm:w-10 sm:h-10 mx-auto mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="text-xs sm:text-sm">No opened leads</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OpenedLeads;

